import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Gift, Percent } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDiscountSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [discountText, setDiscountText] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState("");

  useEffect(() => {
    fetchDiscount();
  }, []);

  const fetchDiscount = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      setLoading(true);
      const response = await axios.get(`${API}/discount`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      setIsActive(data.is_active || false);
      setDiscountText(data.discount_text || "");
      setDiscountPercentage(data.discount_percentage || 15);
    } catch (error) {
      console.error("Error:", error);
      toast.error("İndirim bilgileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem("admin_token");

    if (isActive && !discountText.trim()) {
      toast.error("İndirim metni gerekli");
      return;
    }

    setSaving(true);

    try {
      await axios.put(
        `${API}/admin/discount`,
        {
          is_active: isActive,
          discount_text: discountText,
          discount_percentage: parseInt(discountPercentage) || 15,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("İndirim ayarları güncellendi!");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-teal-400">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-discount-settings">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black text-gradient">İndirim Kampanyası</h2>
        <div className="flex items-center gap-3">
          <Label className="text-teal-400 font-semibold">Kampanya Aktif</Label>
          <Switch
            checked={isActive}
            onCheckedChange={setIsActive}
            className="data-[state=checked]:bg-teal-500"
            data-testid="discount-active-switch"
          />
        </div>
      </div>

      <div className="glass-card-premium rounded-2xl p-8 neon-border space-y-6">
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            <Gift className="w-4 h-4 inline mr-2" />
            Kampanya Metni
          </Label>
          <Input
            value={discountText}
            onChange={(e) => setDiscountText(e.target.value)}
            placeholder="Örn: İlk alışverişte %15 indirim"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
            data-testid="discount-text-input"
          />
        </div>

        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            <Percent className="w-4 h-4 inline mr-2" />
            İndirim Oranı (%)
          </Label>
          <Input
            type="number"
            value={discountPercentage}
            onChange={(e) => setDiscountPercentage(e.target.value)}
            placeholder="15"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
            data-testid="discount-percentage-input"
          />
        </div>

        {isActive && (
          <div className="bg-green-500/10 border-2 border-green-500/30 rounded-2xl p-6">
            <p className="text-xs text-green-400 font-bold uppercase mb-2">Önizleme</p>
            <div className="flex items-center gap-3">
              <Gift className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-white font-bold text-lg">{discountText}</p>
                <p className="text-gray-300 text-sm">Lead formunda gösterilecek</p>
              </div>
            </div>
          </div>
        )}

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-black font-black text-lg shadow-lg neon-glow"
          data-testid="save-discount-btn"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? "Kaydediliyor..." : "İndirim Ayarlarını Kaydet"}
        </Button>
      </div>
    </div>
  );
};

export default AdminDiscountSettings;
